import React, { useCallback, useEffect, useReducer, useState } from "react"
import { atualizarCores, obterCores } from "../../../services/coresAplicativo"
import type { AtualizarCoresPayload } from "../../../services/coresAplicativo"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { CoresAplicativoInicial } from "../models/coresAplicativoModel"
import { CoresAplicativoActionsTypes } from "./coresAplicativoActions"
import { CoresAplicativoContext } from "./CoresAplicativoContext"
import { coresAplicativoReducer } from "./coresAplicativoReducer"

type CoresAplicativoContextProviderProps = {
    children: React.ReactNode
}

export function CoresAplicativoContextProvider({
    children,
}: CoresAplicativoContextProviderProps) {
    const [cores, dispatch] = useReducer(coresAplicativoReducer, CoresAplicativoInicial)
    const [carregando, setCarregando] = useState(false)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        try {
            setLoading(true)
            const response = await obterCores(autorizacao)

            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar as cores do aplicativo")
            } else {
                setErroCarregamento(null)
                dispatch({ type: CoresAplicativoActionsTypes.CARREGAR, payload: response })
            }
        } catch {
            setErroCarregamento("Erro ao carregar as cores do aplicativo")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const salvar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        setCarregando(true)
        try {
            const payload: AtualizarCoresPayload = {
                corSitePrimaria: cores.corSitePrimaria,
                corSiteSecundaria: cores.corSiteSecundaria,
                corSiteBarraFinalizar: cores.corSiteBarraFinalizar,
                corSitePreco: cores.corSitePreco,
                corSiteBotaoConfirmar: cores.corSiteBotaoConfirmar,
                textPesquisaColor: cores.textPesquisaColor,
                corCategoriaCardapio: cores.corCategoriaCardapio,
                corPadrao: cores.corPadrao,
                corPadraoSecundaria: cores.corPadraoSecundaria,
                corTitulosLabelPadrao: cores.corTitulosLabelPadrao,
                corSubtitulos: cores.corSubtitulos,
                corBotoesCabecalho: cores.corBotoesCabecalho,
                corBotoesRodape: cores.corBotoesRodape,
                corTextoDestaque: cores.corTextoDestaque,
                corPreco: cores.corPreco,
                corBackground: cores.corBackground,
                corBackgroundSplash: cores.corBackgroundSplash,
                corFundoCardapio: cores.corFundoCardapio,
                corFundoMenuSelecionado: cores.corFundoMenuSelecionado,
                corBarraEndereco: cores.corBarraEndereco,
                corFonteBotoes: cores.corFonteBotoes,
                corBackgroundPrimeiroAcesso: cores.corBackgroundPrimeiroAcesso,
                corCabecalho: cores.corCabecalho,
                corRodape: cores.corRodape,
                corFundoPanfleto: cores.corFundoPanfleto,
            }

            const response = await atualizarCores(autorizacao, payload)

            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }

            dispatch({ type: CoresAplicativoActionsTypes.CARREGAR, payload: response })
        } finally {
            setCarregando(false)
        }
    }, [cores])

    return (
        <CoresAplicativoContext.Provider
            value={{ cores, dispatch, salvar, carregando, erroCarregamento }}
        >
            <Loading />
            {children}
        </CoresAplicativoContext.Provider>
    )
}
